import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/*
  LURO homepage behaviour.
  - Forms with [data-luro-add] add to cart without leaving the page and tell the theme's
    cart drawer (it listens for CartLinesUpdateEvent), the same way product-form.js does.
    Without this script the form still posts to /cart/add and lands on the cart page.
  - Picking a size in such a form shows that size's picture, when the form names one.
  - A translated review ([data-luro-review]) switches between translation and original.
  - The product page ([data-luro-product]) follows the chosen size and its panel's thumbnails,
    and its purchase pills drive the Subscriptions app block.
  - Elements with [data-luro-reveal] get their children staggered in once on entry.
  - [data-luro-stage] in the hero shows one Sorte at a time and moves on by itself.
  - [data-luro-wall] in the hero is a drifting wall of pouches, paused off screen.
*/

document.documentElement.classList.add('luro-js');

const cartRoutes = () => ({
  add: window.Theme?.routes?.cart_add_url ?? '/cart/add.js',
  cart: `${window.Theme?.routes?.cart_url ?? '/cart'}.js`,
});

/** @param {HTMLFormElement} form */
async function addToCart(form) {
  const button = form.querySelector('[type="submit"]');
  const error = form.querySelector('[data-luro-error]');
  const formData = new FormData(form);

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const quantity = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();

  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: /** @type {string} */ (formData.get('id')), quantity }],
      promise: deferred.promise,
    })
  );

  button?.setAttribute('aria-busy', 'true');
  if (error) error.hidden = true;

  try {
    const response = await fetch(cartRoutes().add, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body: formData,
    }).then((r) => r.json());

    const ajaxCart = await fetch(cartRoutes().cart, { credentials: 'same-origin' }).then((r) => r.json());
    const didError = Boolean(response.status);

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        didError,
        items: ajaxCart.items,
        source: 'luro-home',
        sourceId: form.getAttribute('id'),
        itemCount: quantity,
        productId: form.dataset.productId,
        sections: response.sections,
      },
    });

    if (didError) {
      form.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
      if (error) {
        error.textContent = response.description || response.message;
        error.hidden = false;
      }
    }
  } catch (e) {
    deferred.reject(e);
    form.submit();
  } finally {
    button?.removeAttribute('aria-busy');
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-luro-add]')) return;
  event.preventDefault();
  addToCart(form);
});

// Each size radio carries its picture (luro-buy-form.liquid); the card's img is "<form id>-photo".
// The form's id is read as an attribute: form.id would return its input named "id".
// The whole bean frame fades away with the old picture and comes back with the new one, loaded
// meanwhile: growing in for a bigger size, settling down for a smaller one. A newer pick overtakes
// one still running.
const swaps = new WeakMap();

/** @param {HTMLImageElement} photo @param {string} src @param {string} srcset @param {boolean} bigger */
async function swapPhoto(photo, src, srcset, bigger) {
  const turn = (swaps.get(photo) ?? 0) + 1;
  swaps.set(photo, turn);
  const frame = photo.closest('.luro-blend__photo') ?? photo;
  frame.getAnimations().forEach((a) => a.cancel());

  if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
    photo.srcset = srcset;
    photo.src = src;
    return;
  }

  const next = new Image();
  next.sizes = photo.sizes;
  next.srcset = srcset;
  next.src = src;
  const out = frame.animate(
    [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: `scale(${bigger ? 0.9 : 1.08})` }],
    { duration: 200, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }
  );
  await Promise.all([out.finished.catch(() => {}), next.decode().catch(() => {})]);
  if (swaps.get(photo) !== turn) return;

  photo.srcset = srcset;
  photo.src = src;
  await photo.decode().catch(() => {});
  if (swaps.get(photo) !== turn) return;
  out.cancel();
  frame.animate(
    [{ opacity: 0, transform: `scale(${bigger ? 0.82 : 1.14})` }, { opacity: 1, transform: 'none' }],
    { duration: 460, easing: 'cubic-bezier(0.25, 1.35, 0.5, 1)' }
  );
}

// A Sorte card's price follows the size picked: "From €11,50" becomes that size's price and Grundpreis.
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.form?.matches('[data-luro-add]') || !input.dataset.price) return;
  const card = input.closest('.luro-blend');
  const amount = card?.querySelector('[data-luro-card-amount]');
  const unit = card?.querySelector('[data-luro-card-unit]');
  if (amount) amount.textContent = input.dataset.price;
  if (unit) unit.textContent = input.dataset.unit ?? '';
});

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.form?.matches('[data-luro-add]')) return;
  const photo = document.getElementById(`${input.form.getAttribute('id')}-photo`);
  if (!(photo instanceof HTMLImageElement) || !input.dataset.src) return;
  if (photo.getAttribute('src') === input.dataset.src) return;
  const sizes = [...input.form.querySelectorAll(`input[name="${input.name}"]`)];
  const shown = photo.dataset.shown ?? photo.getAttribute('src');
  const bigger = sizes.indexOf(input) > sizes.findIndex((el) => el.dataset.src === shown);
  photo.dataset.shown = input.dataset.src;
  swapPhoto(photo, input.dataset.src, input.dataset.srcset ?? '', bigger);
});

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element && event.target.closest('[data-luro-review] button');
  if (!(button instanceof HTMLElement)) return;
  const [translation, original] = button.closest('[data-luro-review]')?.querySelectorAll('blockquote') ?? [];
  if (!translation || !original) return;
  const showOriginal = button.getAttribute('aria-pressed') !== 'true';
  translation.hidden = showOriginal;
  original.hidden = !showOriginal;
  // "Translated from …" only holds while the translation is showing
  if (button.previousElementSibling instanceof HTMLElement) button.previousElementSibling.hidden = showOriginal;
  button.setAttribute('aria-pressed', String(showOriginal));
  button.textContent = (showOriginal ? button.dataset.showTranslation : button.dataset.showOriginal) ?? '';
});

// The product page (luro-product.liquid). A size radio sets the hidden "id" input, by attribute as
// well as value, since the Subscriptions app block watches that attribute; then the price, the
// button and the pouch on the panel follow, and the address carries the variant.
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.name !== 'size') return;
  const product = input.closest('[data-luro-product]');
  const form = input.form;
  if (!product || !form) return;

  const id = form.querySelector('input[name="id"]');
  if (id instanceof HTMLInputElement) {
    id.value = input.value;
    id.setAttribute('value', input.value);
  }

  const price = product.querySelector('[data-luro-price]');
  if (price) price.textContent = input.dataset.price ?? '';
  const unit = product.querySelector('[data-luro-unit]');
  if (unit) unit.textContent = input.dataset.unit ?? '';

  const button = form.querySelector('[type="submit"]');
  if (button instanceof HTMLButtonElement) {
    const available = input.dataset.available === 'true';
    button.disabled = !available;
    button.textContent = (available ? button.dataset.addLabel : button.dataset.soldOutLabel) ?? '';
  }

  product.querySelectorAll('.luro-product__pouch').forEach((pouch) => {
    if (pouch instanceof HTMLElement) pouch.classList.toggle('is-shown', pouch.dataset.variantId === input.value);
  });
  showView(product, 0);
  // the app block shows the new variant's options once it has seen the id change
  setTimeout(() => applyPlan(product));

  const url = new URL(location.href);
  url.searchParams.set('variant', input.value);
  history.replaceState(history.state, '', url);
});

// The purchase pills ([data-luro-plan]): one-time or subscription, then the interval. They tick the
// matching option in the Subscriptions app block for the shown variant, which sets selling_plan
// on the form, and put that option's price (with the one-time price struck) in the card.
/** @param {Element} product */
function applyPlan(product) {
  const plan = product.querySelector('[data-luro-plan]');
  if (!plan) return;
  const subscribe = plan.querySelector('input[name="purchase"][value="subscribe"]');
  const once = plan.querySelector('input[name="purchase"][value="once"]');
  const subscribing = subscribe instanceof HTMLInputElement && subscribe.checked;
  const intervals = plan.querySelector('.luro-plan__intervals');
  if (intervals instanceof HTMLElement) intervals.hidden = !subscribing;
  const chosen = subscribing ? plan.querySelector('input[name="plan"]:checked') : null;

  const block = product.querySelector('.luro-product__app section:not(.shopify_subscriptions_app_block--hidden)');
  const option = block?.querySelector(
    chosen instanceof HTMLInputElement
      ? `input[data-selling-plan-id="${chosen.value}"]`
      : 'input[data-radio-type="one_time_purchase"]'
  );
  if (option instanceof HTMLInputElement && !option.checked) option.click();

  const variant = product.querySelector('form input[name="id"]')?.getAttribute('value') ?? '';
  /** @param {Element | null} input @returns {string[]} */
  const pricesOf = (input) => (input instanceof HTMLElement ? JSON.parse(input.dataset.prices ?? '{}')[variant] ?? [] : []);
  const [oncePrice] = pricesOf(once);
  const [price, unit] = pricesOf(chosen ?? once);
  const priceEl = product.querySelector('[data-luro-price]');
  if (priceEl && price) priceEl.textContent = price;
  const unitEl = product.querySelector('[data-luro-unit]');
  if (unitEl && unit !== undefined) unitEl.textContent = unit;
  const compare = product.querySelector('[data-luro-compare]');
  if (compare instanceof HTMLElement) {
    compare.hidden = !chosen;
    compare.textContent = chosen ? oncePrice ?? '' : '';
  }
  const was = product.querySelector('[data-luro-was]');
  if (was instanceof HTMLElement) was.hidden = Boolean(chosen);
}

document.addEventListener('change', (event) => {
  const input = event.target;
  const product = input instanceof Element && input.closest('[data-luro-plan]')?.closest('[data-luro-product]');
  if (product) applyPlan(product);
});

// The panel's thumbnails: the first view is the pouch (when the Sorte has one), then the photos.
/** @param {Element} product @param {number} index */
function showView(product, index) {
  product.querySelectorAll('.luro-product__view').forEach((view, i) => view.classList.toggle('is-active', i === index));
  product.querySelectorAll('.luro-product__thumb').forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === index)));
}

document.addEventListener('click', (event) => {
  const thumb = event.target instanceof Element && event.target.closest('.luro-product__thumb');
  const product = thumb && thumb.closest('[data-luro-product]');
  if (!thumb || !product) return;
  showView(product, [...product.querySelectorAll('.luro-product__thumb')].indexOf(thumb));
});

const reveal = () => {
  const targets = document.querySelectorAll('[data-luro-reveal]:not(.is-in)');
  if (!('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-in'));
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px 20% 0px' }
  );
  targets.forEach((el) => observer.observe(el));
  // Never leave content hidden: anything still waiting after a few seconds is shown.
  setTimeout(() => targets.forEach((el) => el.classList.add('is-in')), 4000);
};

// The band's word fills its box on one line whatever its length and the screen width.
const fitWords = () => {
  document.querySelectorAll('.luro-band__word').forEach((word) => {
    if (!(word instanceof HTMLElement) || !word.parentElement) return;
    word.style.fontSize = '';
    const box = parseFloat(getComputedStyle(word.parentElement).width) -
      parseFloat(getComputedStyle(word.parentElement).paddingLeft) -
      parseFloat(getComputedStyle(word.parentElement).paddingRight);
    const size = parseFloat(getComputedStyle(word).fontSize);
    if (word.scrollWidth > 0) word.style.fontSize = `${Math.floor(size * (box / word.scrollWidth) * 0.98)}px`;
  });
};

// Adds .is-paused while an element is off screen, so its CSS animations stop there.
const pauseOffScreen = (element) => {
  if (!('IntersectionObserver' in window)) return;
  new IntersectionObserver(([entry]) => element.classList.toggle('is-paused', !entry?.isIntersecting)).observe(element);
};

// The hero's pouch wall drifts on CSS alone; it only needs pausing off screen.
const startWall = () => {
  document.querySelectorAll('[data-luro-wall]:not(.is-live)').forEach((wall) => {
    wall.classList.add('is-live');
    pauseOffScreen(wall);
  });
};

// The hero's Sorten stage. The active tab's progress line is the timer: when its animation ends the
// next Sorte comes in, so pausing the line (hover, focus, off screen) pauses the stage, and with
// reduced motion there is no animation and so no autoplay.
const startStage = () => {
  document.querySelectorAll('[data-luro-stage]:not(.is-live)').forEach((stage) => {
    if (!(stage instanceof HTMLElement)) return;
    const slides = [...stage.querySelectorAll('.luro-stage__slide')].filter((el) => el instanceof HTMLElement);
    const tabs = [...stage.querySelectorAll('.luro-stage__tab')];
    let current = 0;

    const show = (index) => {
      if (index === current) return;
      const leaving = slides[current];
      const entering = slides[index];
      if (!leaving || !entering) return;
      leaving.classList.remove('is-active');
      leaving.classList.add('is-leaving');
      leaving.inert = true;
      setTimeout(() => leaving.classList.remove('is-leaving'), 800);
      entering.classList.remove('is-leaving');
      entering.classList.add('is-active');
      entering.inert = false;
      stage.style.setProperty('--tone', entering.dataset.tone ?? '');
      stage.style.setProperty('--ink', entering.dataset.ink ?? '');
      tabs.forEach((tab, i) => tab.setAttribute('aria-pressed', String(i === index)));
      current = index;
    };

    tabs.forEach((tab, i) => tab.addEventListener('click', () => show(i)));
    stage.addEventListener('animationend', (event) => {
      if (event.animationName === 'luro-stage-progress') show((current + 1) % slides.length);
    });
    stage.classList.add('is-live');

    pauseOffScreen(stage);
  });
};

reveal();
document.fonts?.ready.then(fitWords);
fitWords();
startStage();
startWall();
window.addEventListener('resize', fitWords);
document.addEventListener('shopify:section:load', () => {
  reveal();
  fitWords();
  startStage();
  startWall();
});
